import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser, DOCUMENT } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs';

import { TrackingService } from './tracking.service';
import type { TrackingFlow } from './interfaces/tracking-event.interface';

@Injectable({ providedIn: 'root' })
export class TrackingListenerService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);
  private readonly trackingService = inject(TrackingService);

  public startListening(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.listenToRouter();
    this.listenToClicks();
  }

  private listenToRouter(): void {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        const [soloRuta] = event.urlAfterRedirects.split('?');
        const slug = soloRuta.split('/').filter(Boolean).pop() ?? 'home';
        const pageName = slug.replace(/-/g, '_');
        this.trackingService.track({
          name: `page_view_${pageName}`,
          category: 'navigation',
          action: 'page_view',
          section: pageName,
        });
      });
  }

  private listenToClicks(): void {
    this.document.addEventListener(
      'click',
      (domEvent: Event) => {
        const target = domEvent.target as HTMLElement;
        const tracked = target.closest('[data-track-id]');
        if (!(tracked instanceof HTMLElement)) return;

        const dataset = tracked.dataset;

        this.trackingService.track({
          name: dataset['trackId'] ?? '',
          category: dataset['trackComponent'] ?? dataset['trackSection'] ?? 'ui',
          action: 'click',
          ...(dataset['trackFlow'] && { flow: dataset['trackFlow'] as TrackingFlow }),
          ...(dataset['trackSection'] && { section: dataset['trackSection'] }),
          ...(dataset['trackComponent'] && { component: dataset['trackComponent'] }),
          ...(dataset['trackAction'] && { label: dataset['trackAction'] }),
        });
      },
      { capture: true },
    );
  }
}
